import { useEffect, useRef } from 'react';
import { Box, Button, Typography, Paper, CircularProgress, Alert } from '@mui/material';
import { Login as LoginIcon, DeveloperMode } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth, useSnackbarStore } from '@certara/common-react-components';

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { addSnackbar } = useSnackbarStore();
  const autoLoginFired = useRef(false);

  const isDevelopment = import.meta.env.DEV;
  // The unified AuthProvider determines the active provider; if it's "okta"
  // and we're not pending, the Okta config was successfully built. Otherwise
  // CAD is active (or no provider is configured).
  const providerLabel = auth.provider === 'cad' ? 'Certara Admin' : 'Okta';
  const hasConfiguredProvider = auth.provider === 'cad' || !auth.isPending;

  // Whether the page is running inside a WebView2 desktop shell.
  const isWebView2 = typeof (window as unknown as { chrome?: { webview?: unknown } }).chrome?.webview !== 'undefined';

  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate('/');
    }
  }, [auth.isAuthenticated, navigate]);

  // In the WebView2 desktop host, auto-trigger CAD login so the user sees the
  // IdP sign-in form without having to click an intermediate button.
  useEffect(() => {
    if (!isWebView2) return;
    if (auth.provider !== 'cad') return;
    if (auth.isAuthenticated || auth.isPending) return;
    if (autoLoginFired.current) return;
    autoLoginFired.current = true;
    void auth.login();
  }, [isWebView2, auth]);

  const handleLogin = async () => {
    try {
      await auth.login();
    } catch (error) {
      addSnackbar(
        'Login failed. Please try again.',
        'error',
        error instanceof Error ? error.message : String(error),
        true,
      );
    }
  };

  const handleDevBypass = () => {
    window.location.href = '/';
  };

  if (auth.isPending && hasConfiguredProvider) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        data-testid="login-page-loading"
      >
        <CircularProgress data-testid="login-page-loading-spinner" />
      </Box>
    );
  }

  return (
    <Box
      data-testid="login-page"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Paper
        elevation={10}
        sx={{
          p: 6,
          maxWidth: 500,
          textAlign: 'center',
          borderRadius: 2,
        }}
        data-testid="login-page-card"
      >
        <Typography variant="h3" gutterBottom fontWeight="bold" color="primary" data-testid="login-page-title">
          Load
        </Typography>

        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }} data-testid="login-page-welcome">
          Welcome
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }} data-testid="login-page-subtitle">
          Sign in with your {providerLabel} account to continue
        </Typography>

        {hasConfiguredProvider ? (
          <Button
            data-testid="login-page-okta-button"
            variant="contained"
            size="large"
            fullWidth
            startIcon={<LoginIcon />}
            onClick={handleLogin}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              textTransform: 'none',
              background: 'linear-gradient(90deg, rgba(46, 61, 68, 1) 0%, rgba(38, 50, 56, 1) 100%)',
              '&:hover': {
                background: 'linear-gradient(90deg, rgba(38, 50, 56, 1) 0%, rgba(30, 40, 44, 1) 100%)',
              },
            }}
          >
            Sign In with {providerLabel}
          </Button>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="login-page-okta-not-configured-alert">
            Authentication not configured. Running in development mode.
          </Alert>
        )}

        {isDevelopment && !hasConfiguredProvider && (
          <Button
            data-testid="login-page-dev-mode-button"
            variant="outlined"
            size="large"
            fullWidth
            startIcon={<DeveloperMode />}
            onClick={handleDevBypass}
            sx={{
              mt: 0,
              py: 1.5,
              fontSize: '1.1rem',
              textTransform: 'none',
            }}
          >
            Continue in Dev Mode (No Auth)
          </Button>
        )}

        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Load v{window.version}
          </Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            {window.companyName}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
